import './polyfills';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { initSentry } from './utils/error/sentryClient';
import { sentryEnvironment, sentryRelease } from './utils/error/sentryReleaseInfo';
import { scrubSentryEvent } from './utils/error/scrubSentryEvent';
import './index.css';
// KAN-947: invisible 44 x 44 tap area for small controls on touch (touchTapAreaProps).
import './ui/touchTapArea.css';
// KAN-969: typing fields are 16px wherever a finger is the pointer (one rule, one file).
import './ui/fieldTouchFont.css';
// KAN-1141: the number scale's CSS twin (src/ui/figureType.js).
import './ui/figureType.css';
// KAN-946: home dashboard styles load with the entry CSS (see homeStyles.js for why).
import './components/dashboard/home/homeStyles';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { validateEnv } from './utils/env/validateEnv';
import { applyThemeVars } from './ui/theme/applyThemeVars';
import { initThemeController } from './ui/theme/themeController';
import { reportError } from './utils/error/errorReporting';
import { warmFlipSpringMorph } from './ui/morphMotion';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { applySafeTopFloor, installSafeTopFloorWatcher } from './utils/ui/safeTopFloor';
import { stripUpdateCacheBusterFromUrl } from './hooks/useUpdateAvailable';
import { installTypingTracker } from './utils/app/editableTypingTracker';
import { isChunkLoadError, recoverFromChunkLoadError } from './utils/error/chunkLoadRecovery';
import ViewportDebugOverlay from './components/dev/ViewportDebugOverlay';
import LoginDebugOverlay from './components/dev/LoginDebugOverlay';
import { purgeLegacyBulkRunIds } from './utils/invoice/bulkRunId';
import { armBootReturnCover } from './utils/billing/bootReturnCover';
import { installKeyboardDismissBlur } from './utils/ui/keyboardDismissBlur';
// Side-effect only: captures Android's `beforeinstallprompt` at boot so the
// "Install CrewVex" modal (MobileShellHeader) can re-trigger it later — the
// event fires once, early, and is lost if nothing is listening yet.
import './utils/pwa/installPromptStore';
// Role home chunk loaders for the sign-in prefetch (prefetchRoleHome).
import './pages/roleHomeLoaders';

validateEnv();

// Dark mode (KAN-304): theme variables + <html data-theme> BEFORE the first render. The theme is the
// signed-in user's saved preference (device copy) or the device setting; customer pages are always Light
// and ?theme= is a dev-only override (rule: themeMode.js; live state: themeController.js).
applyThemeVars();
initThemeController();

// Clean the `?_v=` cache-buster the update flow appends (useUpdateAvailable's
// performAppUpdate) out of the address bar. Runs before React mounts so the
// router never sees it; best-effort + try/catch'd inside — cannot break boot.
stripUpdateCacheBusterFromUrl();

// Remember fields the user typed into, so the silent app update never reloads over
// half-typed text (KAN-886). One capture listener, idempotent.
installTypingTracker(document);

// Set the iOS top safe-area CSS floor BEFORE React mounts — see safeTopFloor.js
// for why this must be a platform check, not a standalone-vs-browser branch
// computed later inside a component/style module.
applySafeTopFloor();
// The iPhone-browser case is portrait-gated, so the floor must be re-applied
// whenever orientation changes (see safeTopFloor.js for the full rule).
installSafeTopFloorWatcher();

purgeLegacyBulkRunIds(); // KAN-1028: bulk run ids are in-memory only; drop legacy persisted ones
armBootReturnCover(); // KAN-877: a pending Stripe return boots onto an opaque cover, not the page
const root = ReactDOM.createRoot(document.getElementById('root'));

// Disable StrictMode to prevent double-mounts that break Supabase auth with IndexedDB locks
// StrictMode is helpful for detecting side effects, but causes issues with Supabase's persistent storage
// The app is already safe from side effect issues
// ViewportDebugOverlay is a sibling (not inside App/AuthProvider) — it consumes no
// React context and must render above the login screen too; renders null unless
// ?vpdebug=1 is in the URL.
root.render(
  <>
    <App />
    <ViewportDebugOverlay />
    <LoginDebugOverlay />
  </>
);

// Prewarm the framer-motion chunk on idle (OFF the cold-start critical path) so
// the bulk "morph into pill" animation is ready before the user's first morph.
// framer-motion is lazy-loaded out of `main` (see ui/morphMotion.js); this just
// kicks the fetch on idle. Fire-and-forget — degrades gracefully if not landed.
const warmMorph = () => warmFlipSpringMorph();
if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(warmMorph, { timeout: 4000 });
} else {
  setTimeout(warmMorph, 2000);
}

// Sentry loads LAZILY after first paint (its own async chunk) so ~500KB of
// monitoring code never blocks cold-start parse. The window.onerror /
// unhandledrejection handlers below still call reportError() → our DB
// synchronously, so errors during the pre-load window are never lost.
if (process.env.NODE_ENV === 'production') {
  initSentry({
    dsn: 'https://81a542003224d110b7b5d48b846bb811@o4511157398536192.ingest.us.sentry.io/4511157414330368',
    // KAN-895: dev vs prod from the build setting, and the version each error came from.
    environment: sentryEnvironment(),
    release: sentryRelease(),
    enabled: true,
    tracesSampleRate: 0,
    // Redact bearer credentials out of every URL Sentry transmits: the page URL
    // and the navigation/fetch breadcrumbs. RD-EST-RESP-001's public estimate
    // page inherits this init (it runs before React mounts, with no route
    // check) and its GET carries `?token=` by server contract — the fetch
    // breadcrumb is a leak path the page's own address-bar scrub cannot reach.
    // The scrubber is total and always returns the event; see scrubSentryEvent.
    beforeSend: scrubSentryEvent,
  });
}

// Capture uncaught errors and unhandled promise rejections for logging
// Sentry captures these automatically via its global handlers; reportError sends to our own DB as backup.
window.onerror = (message, source, lineno, colno, error) => {
  if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
    return;
  }
  if (isChunkLoadError(error) || (typeof message === 'string' && isChunkLoadError(message))) {
    if (recoverFromChunkLoadError()) return;
  }
  reportError(error || { message, stack: `${source}:${lineno}:${colno}` });
};

window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  if (typeof error === 'string' && error.includes('ResizeObserver loop')) {
    return;
  }
  if (isChunkLoadError(error)) {
    if (recoverFromChunkLoadError()) {
      event.preventDefault();
      return;
    }
  }
  if (error instanceof Error) {
    reportError(error);
  } else if (typeof error === 'string') {
    reportError({ message: error });
  }
});

// iOS Safari: blur the active input when the software keyboard is dismissed (Done /
// swipe-down), confirmed after 300ms (700ms for password / 2FA-code fields, whose
// keyboard -> Strong Password swap reads as closed for a moment). Never leave a field focused.
// Detail in utils/ui/keyboardDismissBlur.js.
installKeyboardDismissBlur(window, document);

// Universal "tap outside to dismiss keyboard" — the web equivalent of an iOS
// UITapGestureRecognizer wired to view.endEditing(true). Guarantees keyboard
// dismissal across the app even when iOS Safari's native accessory Done flakes.
// Pointerdown (not click) so it fires before focus would normally transfer to
// a tapped non-input target.
const FORM_ELEMENT_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
function isEditableElement(el) {
  if (!el) return false;
  if (FORM_ELEMENT_TAGS.has(el.tagName)) return true;
  if (el.isContentEditable) return true;
  return false;
}
document.addEventListener('pointerdown', (e) => {
  const active = document.activeElement;
  if (!isEditableElement(active)) return;
  // Tap landed inside the same editable (or one of its descendants) → keep focus.
  if (active.contains(e.target)) return;
  // Tap on another editable → let normal focus transfer happen, no manual blur.
  if (isEditableElement(e.target)) return;
  active.blur();
}, { passive: true, capture: true });

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Always-fresh strategy (2026-07-13): CrewVex no longer ships an offline caching
// service worker. Every load goes to the network (index.html is served no-cache
// by Vercel; JS/CSS are content-hashed), so users are always on the latest
// deploy — no stale-bundle serving, no "Update now" freeze/loop. On boot we
// proactively remove any worker + caches an OLDER install left behind, so a
// client that reaches this bundle is served fresh from then on. Installs still
// controlled by the old worker heal via the self-destruct src/service-worker.js.
serviceWorkerRegistration.unregisterAndClear();
